import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Outlet, useLocation } from "react-router";

import { ForbiddenState } from "@/components/ForbiddenState";
import { SessionExpiredDialog } from "@/features/auth/SessionExpiredDialog";

import { type AccessRule, allows } from "./access";
import type { Permission } from "./types";
import { useAuth } from "./use-auth";

/** `/login?next=/orders%3Fstatus%3Dready` — where to come back after signing in. */
export function loginPath(next: string): string {
  return next && next !== "/" ? `/login?next=${encodeURIComponent(next)}` : "/login";
}

/**
 * Route-level guard: anonymous → /login?next=… (the page asked for is opened after sign-in);
 * an expired session keeps the page (and its unsaved form) under the sign-in prompt.
 */
export function RequireAuth() {
  const { state } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const here = location.pathname + location.search;

  if (state.status === "loading") {
    return (
      <p role="status" className="p-6 text-muted-foreground">
        {t("app.loading")}
      </p>
    );
  }
  if (state.status === "anonymous") {
    return <Navigate to={loginPath(here)} replace state={{ from: here }} />;
  }
  return (
    <>
      <Outlet />
      {state.status === "expired" ? <SessionExpiredDialog next={here} /> : null}
    </>
  );
}

interface RequirePermissionProps {
  permission: Permission;
  children: ReactNode;
}

/** Element-level guard: shows a plain-language "no access" message instead of the content. */
export function RequirePermission({ permission, children }: RequirePermissionProps) {
  const { can } = useAuth();
  if (!can(permission)) {
    return <ForbiddenState />;
  }
  return children;
}

interface RequireAccessProps {
  rule: AccessRule | undefined;
  children: ReactNode;
}

/** Same as RequirePermission for a route rule (`permission` and/or `anyOf`). */
export function RequireAccess({ rule, children }: RequireAccessProps) {
  const { can } = useAuth();
  if (!allows(rule, can)) {
    return <ForbiddenState />;
  }
  return children;
}
